import { useEffect, useState } from 'react';
import { useGameStore } from '../stores/gameStore';
import { StoreItem, StoreCategory, SoftwareType, SOFTWARE_ROLE, formatMhz, formatMb, formatMbit, calcTotals, usedHddMb, filesHddMb, infectionsHddMb, getSoftwareResources, isIpChangePending, ipChangeMsRemaining } from '../types';
import { storeApi } from '../services/api';
import { useTransferStore } from '../stores/transferStore';

const CATEGORY_LABELS: Record<StoreCategory, string> = {
  [StoreCategory.Server]: 'Servers',
  [StoreCategory.CpuUpgrade]: 'CPU',
  [StoreCategory.HddUpgrade]: 'HDD',
  [StoreCategory.RamUpgrade]: 'RAM',
  [StoreCategory.InternetUpgrade]: 'Internet',
  [StoreCategory.Software]: 'Software',
  [StoreCategory.EncryptedHdUpgrade]: 'Encrypted HD',
};

const ROLE_COLORS: Record<'ATK' | 'DEF' | 'UTL', string> = {
  ATK: '#f87171',
  DEF: '#4ade80',
  UTL: '#60a5fa',
};

function formatValue(category: StoreCategory, value: number): string {
  switch (category) {
    case StoreCategory.CpuUpgrade: return `+${formatMhz(value)}`;
    case StoreCategory.HddUpgrade: return `+${formatMb(value)}`;
    case StoreCategory.RamUpgrade: return `+${formatMb(value)}`;
    case StoreCategory.InternetUpgrade: return `+${formatMbit(value)}`;
    case StoreCategory.EncryptedHdUpgrade: return `+${formatMb(value)}`;
    case StoreCategory.Software: return `v${(value / 10).toFixed(1)}`;
    default: return '';
  }
}

export default function Store() {
  const { player, loadPlayer, infections } = useGameStore();
  const { addTransfer } = useTransferStore();

  const [catalog, setCatalog] = useState<StoreItem[]>([]);
  const [activeCategory, setActiveCategory] = useState<StoreCategory>(StoreCategory.CpuUpgrade);
  const [swRoleFilter, setSwRoleFilter] = useState<'ALL' | 'ATK' | 'DEF' | 'UTL'>('ALL');
  const [selectedServerId, setSelectedServerId] = useState<string>('');
  const [buying, setBuying] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);

  // Load on mount so a direct page load / F5 works (data comes via the session
  // cookie; an invalid cookie is handled by the global 401 interceptor).
  useEffect(() => {
    storeApi.getCatalog().then(r => setCatalog(r.data));
    loadPlayer();
  }, [loadPlayer]);

  useEffect(() => {
    if (player?.servers.length && !selectedServerId) {
      setSelectedServerId(player.servers[0].id);
    }
  }, [player, selectedServerId]);

  const needsServer = (cat: StoreCategory) =>
    cat === StoreCategory.CpuUpgrade ||
    cat === StoreCategory.HddUpgrade ||
    cat === StoreCategory.RamUpgrade;

  const getSoftwareOwned = (softwareType?: SoftwareType) => {
    if (!softwareType || !player) return null;
    return player.software.find(s => s.type === softwareType) ?? null;
  };

  const getBuyState = (item: StoreItem): { label: string; disabled: boolean; isOwned: boolean; noSpace: boolean } => {
    const canAfford = (player?.credits ?? 0) >= item.price;
    if (item.category === StoreCategory.Software && item.softwareType) {
      const owned = getSoftwareOwned(item.softwareType);
      const newVersion = item.value / 10;
      if (owned && owned.version >= newVersion) return { label: 'OWNED', disabled: true, isOwned: true, noSpace: false };
      // HDD space check
      let noSpace = false;
      if (player) {
        const totals = calcTotals(player.servers);
        const newSize = getSoftwareResources(item.softwareType, newVersion).hddMb;
        // Match the backend capacity check: software + files + hosted infections.
        const currentUsed = usedHddMb(player.software) + filesHddMb(player.files ?? []) + infectionsHddMb(infections);
        const oldSize = (owned?.isDownloaded) ? getSoftwareResources(item.softwareType, owned.version).hddMb : 0;
        noSpace = (currentUsed - oldSize + newSize) > totals.hdd;
      }
      if (!canAfford || noSpace) return { label: owned ? 'UPGRADE' : 'BUY', disabled: true, isOwned: false, noSpace };
      return { label: owned ? 'UPGRADE' : 'BUY', disabled: false, isOwned: false, noSpace: false };
    }
    return { label: 'BUY', disabled: !canAfford, isOwned: false, noSpace: false };
  };

  const handleBuy = async (item: StoreItem) => {
    if (ipLocked) { setMessage({ text: 'Store locked — IP change in progress.', ok: false }); return; }
    setBuying(item.id);
    setMessage(null);
    try {
      const serverId = needsServer(item.category) ? selectedServerId : undefined;
      const response = await storeApi.buyItem(item.id, serverId);
      const updatedPlayer = response.data;
      await loadPlayer();
      if (item.category === StoreCategory.Software && item.softwareType) {
        const version = item.value / 10;
        const newSw = updatedPlayer?.software?.find(
          (s: { type: string; version: number; isDownloaded: boolean }) =>
            s.type === item.softwareType && s.version === version && !s.isDownloaded
        );
        if (newSw) {
          const { hddMb } = getSoftwareResources(item.softwareType, version);
          addTransfer({
            id: `sw-${newSw.id}`,
            kind: 'software',
            softwareId: newSw.id,
            softwareType: item.softwareType,
            name: item.name,
            version,
            fileSizeMb: hddMb,
            downloadedMb: 0,
          });
          setMessage({ text: `Downloading: ${item.name} (${hddMb} MB)`, ok: true });
        } else {
          setMessage({ text: `Purchased: ${item.name}`, ok: true });
        }
      } else {
        setMessage({ text: `Purchased: ${item.name}`, ok: true });
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message ?? 'Purchase failed';
      setMessage({ text: msg, ok: false });
    } finally {
      setBuying(null);
    }
  };

  const items = catalog
    .filter(i => i.category === activeCategory)
    .filter(i => {
      if (activeCategory !== StoreCategory.Software || swRoleFilter === 'ALL') return true;
      return i.softwareType && SOFTWARE_ROLE[i.softwareType] === swRoleFilter;
    });

  const ipLocked = player ? isIpChangePending(player.ipChangePendingAt) : false;
  const ipMsLeft = player ? ipChangeMsRemaining(player.ipChangePendingAt) : 0;
  const ipMinLeft = Math.ceil(ipMsLeft / 60000);

  return (
    <div>
      <h1 style={{ color: 'var(--primary-color)', marginBottom: '0.5rem' }}>Store</h1>

      {ipLocked && (
        <div style={{
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          background: '#1a1600',
          border: '1px solid #fbbf24',
          borderRadius: '4px',
          color: '#fbbf24',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
        }}>
          IP change in progress — store is locked. Unlocks in ~{ipMinLeft} min.
        </div>
      )}

      {player && (() => {
        const totals = calcTotals(player.servers);
        const hddUsed = usedHddMb(player.software) + filesHddMb(player.files ?? []) + infectionsHddMb(infections);
        const hddPct = totals.hdd > 0 ? Math.min(100, (hddUsed / totals.hdd) * 100) : 0;
        const hddColor = hddPct > 90 ? '#f87171' : hddPct > 70 ? '#fbbf24' : '#4ade80';
        return (
          <div style={{ fontFamily: 'monospace', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', gap: '2rem', marginBottom: '0.5rem' }}>
              <span style={{ color: '#888' }}>Credits: <span style={{ color: '#4ade80' }}>{player.credits.toLocaleString()}</span></span>
              <span style={{ color: '#888' }}>HDD: <span style={{ color: hddColor }}>{formatMb(hddUsed)} / {formatMb(totals.hdd)}</span></span>
            </div>
            <div style={{ height: '4px', background: '#1a1a2e', borderRadius: '2px', overflow: 'hidden', maxWidth: '300px' }}>
              <div style={{ height: '100%', width: `${hddPct}%`, background: hddColor, borderRadius: '2px' }} />
            </div>
          </div>
        );
      })()}

      {message && (
        <div style={{
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          background: message.ok ? '#052e16' : '#2d0a0a',
          border: `1px solid ${message.ok ? '#16a34a' : '#dc2626'}`,
          borderRadius: '4px',
          color: message.ok ? '#4ade80' : '#f87171',
          fontFamily: 'monospace',
        }}>
          {message.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {Object.values(StoreCategory).filter(cat => cat !== StoreCategory.Server).map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            style={{
              background: activeCategory === cat ? 'var(--primary-color)' : '#0d1117',
              color: activeCategory === cat ? '#000' : 'var(--primary-color)',
              border: '1px solid var(--primary-color)',
              padding: '0.4rem 1rem',
              borderRadius: '4px',
              cursor: 'pointer',
              fontFamily: 'monospace',
              fontWeight: activeCategory === cat ? 'bold' : 'normal',
            }}
          >
            {CATEGORY_LABELS[cat]}
          </button>
        ))}
      </div>

      {activeCategory === StoreCategory.Software && (
        <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.25rem' }}>
          {(['ALL', 'ATK', 'DEF', 'UTL'] as const).map(f => {
            const color = f === 'ALL' ? '#e2e8f0' : ROLE_COLORS[f as 'ATK' | 'DEF' | 'UTL'];
            const active = swRoleFilter === f;
            return (
              <button
                key={f}
                onClick={() => setSwRoleFilter(f)}
                style={{
                  padding: '0.25rem 0.9rem',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  fontWeight: active ? 'bold' : 'normal',
                  cursor: 'pointer',
                  background: active ? color : 'transparent',
                  color: active ? '#000' : color,
                  border: `1px solid ${color}`,
                }}
              >
                {f}
              </button>
            );
          })}
        </div>
      )}

      {needsServer(activeCategory) && player && player.servers.length > 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={{ color: '#888', fontFamily: 'monospace', fontSize: '0.85rem', marginRight: '0.75rem' }}>
            Target server:
          </label>
          <select
            value={selectedServerId}
            onChange={e => setSelectedServerId(e.target.value)}
            style={{
              background: '#0d1117',
              color: '#e2e8f0',
              border: '1px solid #333',
              padding: '0.4rem 0.75rem',
              borderRadius: '4px',
              fontFamily: 'monospace',
            }}
          >
            {player.servers.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      )}

      <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
        {items.map(item => {
          const canAfford = (player?.credits ?? 0) >= item.price;
          const isLoading = buying === item.id;
          const buyState = getBuyState(item);
          const role = item.softwareType ? SOFTWARE_ROLE[item.softwareType] : null;
          const owned = getSoftwareOwned(item.softwareType ?? undefined);
          const swVersion = item.category === StoreCategory.Software ? item.value / 10 : 0;
          const swSize = item.softwareType ? getSoftwareResources(item.softwareType, swVersion).hddMb : 0;

          return (
            <div key={item.id} className="card" style={{ opacity: buyState.isOwned ? 0.5 : canAfford ? 1 : 0.65 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.4rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {role && (
                    <span style={{
                      fontFamily: 'monospace',
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      color: ROLE_COLORS[role],
                      border: `1px solid ${ROLE_COLORS[role]}`,
                      padding: '0 0.3rem',
                      borderRadius: '3px',
                    }}>
                      {role}
                    </span>
                  )}
                  <h3 style={{ color: 'var(--primary-color)', fontFamily: 'monospace', margin: 0 }}>{item.name}</h3>
                </div>
                {item.category !== StoreCategory.Server && (
                  <span style={{ color: '#4ade80', fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'nowrap', marginLeft: '0.5rem' }}>
                    {formatValue(item.category, item.value)}
                  </span>
                )}
              </div>

              {buyState.isOwned && (
                <div style={{ fontSize: '0.75rem', color: '#4ade80', fontFamily: 'monospace', marginBottom: '0.3rem' }}>
                  ✓ Installed v{owned?.version.toFixed(1)} — upgrade from the Software panel
                </div>
              )}
              {buyState.noSpace && (
                <div style={{ fontSize: '0.75rem', color: '#f87171', fontFamily: 'monospace', marginBottom: '0.3rem' }}>
                  ✗ Insufficient HDD space ({formatMb(swSize)} required)
                </div>
              )}

              <p style={{ color: '#888', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>{item.description}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontFamily: 'monospace', color: canAfford ? '#e2e8f0' : '#f87171', fontWeight: 'bold' }}>
                  {item.price.toLocaleString()} cr
                </span>
                <button
                  onClick={() => !buyState.disabled && !isLoading && !buying && handleBuy(item)}
                  disabled={buyState.disabled || isLoading || buying !== null}
                  style={{
                    background: buyState.isOwned ? '#1a2a1a' : canAfford ? 'var(--primary-color)' : '#333',
                    color: buyState.isOwned ? '#4ade80' : canAfford ? '#000' : '#666',
                    border: buyState.isOwned ? '1px solid #4ade80' : 'none',
                    padding: '0.4rem 1rem',
                    borderRadius: '4px',
                    cursor: !buyState.disabled && !buying ? 'pointer' : 'not-allowed',
                    fontFamily: 'monospace',
                    fontWeight: 'bold',
                    minWidth: '5rem',
                  }}
                >
                  {isLoading ? '...' : buyState.label}
                </button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <p style={{ color: '#888', fontFamily: 'monospace' }}>No items in this category.</p>
        )}
      </div>
    </div>
  );
}
